import { defineStore } from 'pinia'
import Provider from '@/provider'
import {
  SettingPage,
  SettingShop,
  LocationRestricted,
  ABTest,
  MainSettings,
  PopupSettings,
  Freshchat,
  ABTestSettings,
  PopupSetting,
  CampaignDiscount,
  TagDiscountCodeApplication,
  StyleSettings,
  SettingsType,
  ScriptSettings,
  ProductSettings,
} from '@/types'
import Logger from '@/services/log'
import {
  SETTING_TYPE_AB_TEST,
  SETTING_TYPE_MAIN,
  SETTING_TYPE_POPUP,
  SETTING_TYPE_STYLE,
  SETTING_TYPE_SCRIPT,
  SETTING_TYPE_PRODUCT_PAGE,
  SETTING_TYPE_PRODUCT_LANDING_PAGE,
  SETTING_TYPE_CAMPAIGN_DISCOUNT,
} from '@/utils'
import { UserSegments } from '@/types/user'
import { PromotionBarSetings } from '@/types/promotionBar'
import CacheService, { getJSONfromCacheResponse } from '@/services/cache'

export interface SettingState {
  shop?: SettingShop
  pages?: SettingPage
  locationRestricted?: LocationRestricted
  popups?: PopupSetting[]
  freshchat?: Freshchat
  abTests?: ABTest[]
  styleSettings?: StyleSettings
  scriptSettings?: ScriptSettings
  campaigns?: CampaignDiscount[]
  tagDiscountCodes?: TagDiscountCodeApplication[]
  freeShippingMessage: string
  productSKUsExcludeCampaignDiscount?: string
  userSegments?: UserSegments
  promotionBar?: PromotionBarSetings
  productPage?: ProductSettings
  productLandingPage?: ProductSettings
  baseProductPage?: ProductSettings
  basePages?: any
}

const useSettingStore = defineStore('setting', {
  state: (): SettingState => ({
    shop: undefined,
    pages: undefined,
    locationRestricted: undefined,
    popups: undefined,
    freshchat: undefined,
    abTests: undefined,
    campaigns: undefined,
    tagDiscountCodes: undefined,
    freeShippingMessage: '',
    productSKUsExcludeCampaignDiscount: '',
    styleSettings: undefined,
    scriptSettings: undefined,
    userSegments: undefined,
    promotionBar: undefined,
    productPage: undefined,
    productLandingPage: undefined,
    baseProductPage: undefined,
    basePages: undefined,
  }),
  getters: {
    tmProductTags(state) {
      return state.shop?.productTagsTM || []
    },
  },
  actions: {
    async loadSettings(isForceLoadNewData?: boolean = false) {
      let settings, baseSettings
      const cacheKey = 'setting_loadSettings'
      if (!isForceLoadNewData) {
        const cacheResponse = await CacheService.instance?.get(cacheKey)
        const settingFromCache = getJSONfromCacheResponse(cacheResponse)
        if (settingFromCache?.settings && settingFromCache?.baseSettings) {
          settings = settingFromCache?.settings
          baseSettings = settingFromCache?.baseSettings
        }
      }
      if (!settings || !baseSettings) {
        try {
          const provider = await Provider.getInstance()
          const [settingsRes, baseSettingsRes] = await Promise.all([
            provider.getCoreSettings(),
            provider.getBaseCoreSettings(),
          ])

          if (settingsRes) settings = settingsRes
          if (baseSettingsRes) baseSettings = baseSettingsRes
        } catch {}

        if (!settings && !baseSettings) {
          const sanitySettings = await import(
            // @ts-ignore
            '@/store/setting/sanitySettings.ts'
          )

          settings = sanitySettings.SETTINGS
          baseSettings = sanitySettings.BASE_SETTINGS
        }

        if (settings && baseSettings) {
          const allSettings = { settings, baseSettings }
          CacheService.instance?.set(
            cacheKey,
            allSettings,
            30 * 60 * 1000 // 30 phut
          )
        } else {
          CacheService.instance?.delete(cacheKey)
        }
      }

      Logger.log('Store load settings', settings, 2)
      Logger.log('Store load baseSettings', baseSettings, 2)

      const baseMainSettings = findSettingByType(
        baseSettings,
        SETTING_TYPE_MAIN
      ) as MainSettings | undefined

      if (baseMainSettings) {
        this.basePages = baseMainSettings.pages
        this.locationRestricted = baseMainSettings.locationRestricted
      }

      const mainSettings = findSettingByType(settings, SETTING_TYPE_MAIN) as
        | MainSettings
        | undefined

      if (mainSettings) {
        this.shop = mainSettings.shop
        this.pages = mainSettings.pages
        this.freshchat = mainSettings.freshchat
        this.userSegments = mainSettings.userSegments
        this.promotionBar = mainSettings.promotionBar
      }

      const popupSettings = findSettingByType(settings, SETTING_TYPE_POPUP) as
        | PopupSettings
        | undefined
      if (popupSettings) {
        this.popups = popupSettings.popups
      }

      const abTestSettings = findSettingByType(
        settings,
        SETTING_TYPE_AB_TEST
      ) as ABTestSettings | undefined
      if (abTestSettings) {
        this.abTests = abTestSettings.abTests
      }

      const styleSettings = findSettingByType(settings, SETTING_TYPE_STYLE) as
        | StyleSettings
        | undefined
      if (styleSettings) {
        this.styleSettings = styleSettings
      }

      const scriptSettings = findSettingByType(
        settings,
        SETTING_TYPE_SCRIPT
      ) as ScriptSettings | undefined

      if (scriptSettings) {
        this.scriptSettings = scriptSettings
      }

      const campaignDiscountSettings = findSettingByType(
        settings,
        SETTING_TYPE_CAMPAIGN_DISCOUNT
      ) as ScriptSettings | undefined

      if (campaignDiscountSettings) {
        this.campaigns = campaignDiscountSettings.campaignsDiscount?.campaigns
        this.freeShippingMessage =
          campaignDiscountSettings.campaignsDiscount.freeShippingMessage
        this.productSKUsExcludeCampaignDiscount =
          campaignDiscountSettings.campaignsDiscount.productSKUsExcludeCampaignDiscountBF9
        this.tagDiscountCodes =
          campaignDiscountSettings.campaignsDiscount.tagDiscountCodes
      }
    },

    async loadProductPageSettings(isForceLoadNewData = false) {
      if (!isForceLoadNewData && this.baseProductPage && this.productPage) return
      if (isForceLoadNewData) {
        try {
          const provider = await Provider.getInstance()
          const [prodRes, baseProdRes] = await Promise.all([
            provider.getProductPageSettings(),
            provider.getBaseProductPageSettings(),
          ])

          const productPageSettings = findSettingByType(
            prodRes,
            SETTING_TYPE_PRODUCT_PAGE
          ) as ProductSettings | undefined
          if (productPageSettings) {
            this.productPage = productPageSettings?.productPage
          }

          const baseProductPageSettings = findSettingByType(
            baseProdRes,
            SETTING_TYPE_PRODUCT_PAGE
          ) as ProductSettings | undefined
          if (baseProductPageSettings) {
            this.baseProductPage = baseProductPageSettings?.productPage
          }
        } catch (error) {
          Logger.error('Failed to load product page settings', { error })
        }
        return
      }

      const cacheKey = 'setting_productPage'
      let cachedProductPage = null
      let cachedBaseProductPage = null

      // Try Redis cache first (cross-instance, reduces API calls)
      const cacheResponse = await CacheService.instance?.get(cacheKey)
      const cached = getJSONfromCacheResponse(cacheResponse)
      if (cached?.productPage && cached?.baseProductPage) {
        cachedProductPage = cached.productPage
        cachedBaseProductPage = cached.baseProductPage
      }

      // Cache HIT: use cached values
      if (cachedProductPage && cachedBaseProductPage) {
        this.productPage = cachedProductPage
        this.baseProductPage = cachedBaseProductPage
        return
      }

      // Cache MISS: call API
      try {
        const provider = await Provider.getInstance()
        const [prodRes, baseProdRes] = await Promise.all([
          provider.getProductPageSettings(),
          provider.getBaseProductPageSettings(),
        ])

        const productPageSettings = findSettingByType(
          prodRes,
          SETTING_TYPE_PRODUCT_PAGE
        ) as ProductSettings | undefined
        if (productPageSettings) {
          this.productPage = productPageSettings?.productPage
        }

        const baseProductPageSettings = findSettingByType(
          baseProdRes,
          SETTING_TYPE_PRODUCT_PAGE
        ) as ProductSettings | undefined
        if (baseProductPageSettings) {
          this.baseProductPage = baseProductPageSettings?.productPage
        }

        if (this.productPage && this.baseProductPage) {
          CacheService.instance?.set(
            cacheKey,
            { productPage: this.productPage, baseProductPage: this.baseProductPage },
            60 * 60 * 1000
          )
        }
      } catch (error) {
        Logger.error('Failed to load product page settings', { error })
      }
    },

    async loadLandingPageSettings(isForceLoadNewData = false) {
      if (!isForceLoadNewData && this.productLandingPage) return
      if (isForceLoadNewData) {
        try {
          const provider = await Provider.getInstance()
          const lpRes = await provider.getProductLandingPageSettings()

          const lpSettings = findSettingByType(
            lpRes,
            SETTING_TYPE_PRODUCT_LANDING_PAGE
          ) as ProductSettings | undefined
          if (lpSettings) {
            this.productLandingPage = lpSettings?.productLandingPage
          }
          // Do NOT cache when force reload - user explicitly wants fresh data
        } catch (error) {
          Logger.error('Failed to load landing page settings', { error })
        }
        return
      }

      const cacheKey = 'setting_landingPage'
      let cachedLandingPage = null

      const cacheResponse = await CacheService.instance?.get(cacheKey)
      const cached = getJSONfromCacheResponse(cacheResponse)
      if (cached?.productLandingPage) {
        cachedLandingPage = cached.productLandingPage
      }

      // Cache HIT: use cached value
      if (cachedLandingPage) {
        this.productLandingPage = cachedLandingPage
        return
      }

      // Cache MISS: call API
      try {
        const provider = await Provider.getInstance()
        const lpRes = await provider.getProductLandingPageSettings()

        const lpSettings = findSettingByType(
          lpRes,
          SETTING_TYPE_PRODUCT_LANDING_PAGE
        ) as ProductSettings | undefined
        if (lpSettings) {
          this.productLandingPage = lpSettings?.productLandingPage
        }

        if (this.productLandingPage) {
          CacheService.instance?.set(
            cacheKey,
            { productLandingPage: this.productLandingPage },
            30 * 60 * 1000
          )
        }
      } catch (error) {
        Logger.error('Failed to load landing page settings', { error })
      }
    },
  },
})

function findSettingByType(settings: Array<SettingsType>, type: string) {
  return settings.find((elm) => elm._type === type)
}

export default useSettingStore
